import { jsPDF } from 'jspdf';

// Constants for print quality
const TARGET_DPI = 300;
const PIXELS_PER_MM = TARGET_DPI / 25.4;

// Helper function to load an image
const loadImage = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = url;
    });
};

/*
 * Renders a single Polaroid Image
 * Logic:
 * 1. Draw Image Full Bleed (matching the Editor's Full Card view)
 * 2. Draw White Frame OVER the image (Masking)
 * This ensures the positioning in the PDF exactly matches the "Full Card" view in the Editor.
 */
const renderPolaroidImage = async (badgeData, designWidthMM, designHeightMM, applyPolaroidFrame = true) => {
    // Canvas covers the FULL Card Size
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(designWidthMM * PIXELS_PER_MM);
    canvas.height = Math.round(designHeightMM * PIXELS_PER_MM);
    const ctx = canvas.getContext('2d');

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // 1. Draw Background (White Card)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 2. Draw Image (Full Bleed - Matching Editor Coordinates)
    const imageLayer = badgeData?.layers?.find(layer => layer.type === 'image');

    if (imageLayer && imageLayer.url) {
        try {
            const img = await loadImage(imageLayer.url);

            // User Settings from Editor
            const fitMode = imageLayer.fitMode || 'cover';
            const scale = imageLayer.scale || 1;
            const offsetXPercent = imageLayer.offsetX || 0;
            const offsetYPercent = imageLayer.offsetY || 0;

            // Dimensions of the CANVAS (The "Badge Area" in Editor terms)
            const areaW = canvas.width;
            const areaH = canvas.height;

            const imgNaturalW = img.naturalWidth;
            const imgNaturalH = img.naturalHeight;
            const imgAspect = imgNaturalW / imgNaturalH;
            const areaAspect = areaW / areaH;

            let drawW, drawH;

            // Cover Logic (Match EditorCanvas behavior for 'cover')
            // Note: EditorCanvas might behave slightly differently for 'contain' but 'cover' is default.
            if (fitMode === 'contain') {
                if (imgAspect > areaAspect) {
                    drawW = areaW;
                    drawH = drawW / imgAspect;
                } else {
                    drawH = areaH;
                    drawW = drawH * imgAspect;
                }
            } else {
                // Cover
                if (imgAspect > areaAspect) {
                    // Image is wider than canvas -> Height fills canvas
                    drawH = areaH * scale;
                    drawW = drawH * imgAspect;
                } else {
                    // Image is taller than canvas -> Width fills canvas
                    drawW = areaW * scale;
                    drawH = drawW / imgAspect;
                }
            }

            // If scale is applied in 'contain', we normally start from 'contain' base. 
            // Editor logic applies scale multiplier to whatever base size it calculated.
            if (fitMode === 'contain') {
                drawW *= scale;
                drawH *= scale;
            }

            // Calculate Position
            // EditorCanvas: offsetX/Y are percentage of the DRAWN size
            // This allows panning relative to the zoomed image size.
            const offsetXPx = (offsetXPercent * drawW) / 100;
            const offsetYPx = (offsetYPercent * drawH) / 100;

            const centerX = areaW / 2;
            const centerY = areaH / 2;

            const drawX = centerX - drawW / 2 + offsetXPx;
            const drawY = centerY - drawH / 2 + offsetYPx;

            // Clip to Full Card
            ctx.save();
            ctx.beginPath();
            ctx.rect(0, 0, areaW, areaH);
            ctx.clip();

            ctx.drawImage(img, drawX, drawY, drawW, drawH);
            ctx.restore();

        } catch (error) {
            console.error('Error loading polaroid image:', error);
        }
    }

    // 3. Draw Polaroid Frame Overlay (if enabled)
    // This "Masks" the edges of the image to create the frame.
    if (applyPolaroidFrame) {
        const sideMarginMM = designWidthMM * 0.06;
        const topMarginMM = designHeightMM * 0.06;
        const bottomMarginMM = designHeightMM * 0.22;

        const sidePx = sideMarginMM * PIXELS_PER_MM;
        const topPx = topMarginMM * PIXELS_PER_MM;
        const bottomPx = bottomMarginMM * PIXELS_PER_MM;

        ctx.fillStyle = '#ffffff';

        // Top Block
        ctx.fillRect(0, 0, canvas.width, topPx);
        // Left Block
        ctx.fillRect(0, 0, sidePx, canvas.height);
        // Right Block
        ctx.fillRect(canvas.width - sidePx, 0, sidePx, canvas.height);
        // Bottom Block (Thick)
        ctx.fillRect(0, canvas.height - bottomPx, canvas.width, bottomPx);

        // Inner Light Border (Stroke around the "hole")
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 1;
        const imgAreaX = sidePx;
        const imgAreaY = topPx;
        const imgAreaW = canvas.width - (sidePx * 2);
        const imgAreaH = canvas.height - topPx - bottomPx;
        ctx.strokeRect(imgAreaX, imgAreaY, imgAreaW, imgAreaH);
    }

    // 4. Draw Outer Card Border
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/png');
};

export async function generatePolaroidPDF({ badge, badges, orientation, showGuides, showFrame }) {
    try {
        // A4 in mm
        const A4_WIDTH_MM = 210;
        const A4_HEIGHT_MM = 297;
        const MARGIN_MM = 10;
        const GUTTER_MM = 2; // Tighter gutter for photos

        // Create PDF
        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            compress: false
        });

        // Determine Logic Dimensions
        // For Polaroids, we treat 'designWidth' as the CARD SIZE.
        let cardWidthMM, cardHeightMM;

        // Handle Orientation Swap Logic explicitly
        // Default definition is Landscape in file, but if user chose Portrait, we swap.
        if (orientation === 'portrait') {
            // Ensure dimensions are Tall
            cardWidthMM = Math.min(badge.designWidth, badge.designHeight);
            cardHeightMM = Math.max(badge.designWidth, badge.designHeight);
        } else {
            // Ensure dimensions are Wide
            cardWidthMM = Math.max(badge.designWidth, badge.designHeight);
            cardHeightMM = Math.min(badge.designWidth, badge.designHeight);
        }

        console.log('=== POLAROID PDF GENERATION ===');
        console.log(`Card Size: ${cardWidthMM}mm x ${cardHeightMM}mm`);

        // Calculate layout
        const availableWidthMM = A4_WIDTH_MM - (2 * MARGIN_MM);
        const availableHeightMM = A4_HEIGHT_MM - (2 * MARGIN_MM);

        const cols = Math.floor((availableWidthMM + GUTTER_MM) / (cardWidthMM + GUTTER_MM));
        const rows = Math.floor((availableHeightMM + GUTTER_MM) / (cardHeightMM + GUTTER_MM));

        const totalGridWidthMM = cols * cardWidthMM + (cols - 1) * GUTTER_MM;
        const totalGridHeightMM = rows * cardHeightMM + (rows - 1) * GUTTER_MM;
        const startXMM = (A4_WIDTH_MM - totalGridWidthMM) / 2;
        const startYMM = (A4_HEIGHT_MM - totalGridHeightMM) / 2;

        // Render Grid
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                const badgeIndex = row * cols + col;

                const xMM = startXMM + col * (cardWidthMM + GUTTER_MM);
                const yMM = startYMM + row * (cardHeightMM + GUTTER_MM);

                // Get Badge Data (Cyclical if fewer badges than grid slots)
                // Or if we implemented multi-badge selection, use that.
                // For now, assuming single design repeated or list of badges.
                const badgeData = badges[badgeIndex % badges.length] || badges[0];

                // Render Polaroid Card
                const polaroidImage = await renderPolaroidImage(
                    badgeData, cardWidthMM, cardHeightMM, showFrame
                );

                pdf.addImage(polaroidImage, 'PNG', xMM, yMM, cardWidthMM, cardHeightMM);

                // Guides
                if (showGuides) {
                    // Cut Line (Outer Edge of Card)
                    pdf.setDrawColor(200, 200, 200); // Light Grey
                    pdf.setLineWidth(0.1);
                    pdf.setLineDashPattern([2, 2], 0);
                    pdf.rect(xMM, yMM, cardWidthMM, cardHeightMM);
                }
            }
        }

        // Footer Info
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Polaroid Photo Sheet - Print at 100% Scale', 105, A4_HEIGHT_MM - 10, { align: 'center' });

        // Save
        const filename = `polaroids_${badge.id}_${Date.now()}.pdf`;
        pdf.save(filename);

        console.log(`Polaroid PDF saved: ${filename}`);
        return filename;

    } catch (error) {
        console.error('Polaroid PDF Error:', error);
        throw error;
    }
}
